import React, { useEffect, useMemo, useState } from 'react';
import { ReactComponent as Swap } from '../../svgs/swap.svg';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import BuyWaterSteps from '../../components/BuyWaterComponents/BuyWaterSteps';
import BackButton from '../../components/GeneralComponents/BackButton';
import {
  convertAmountToCubicMetres,
  getInputClasses,
  routes,
} from '../../helper/misc';
import { NumericFormat } from 'react-number-format';
import Button from '../../components/GeneralComponents/Button';
import { useAuth } from '../../helper/auth';
import { usePurchases } from '../../helper/purchases';
import Loader from '../../components/GeneralComponents/Loader';
import classNames from 'classnames';
import authAPIs from '../../api/auth';
import payments from '../../api/payments';
import iotAPI from '../../api/iot';
import { onAPIError } from '../../helper/functions';
import dropdownIcon from '../../images/dropdown.png';
import { cubicMetres } from '../../constants';

const MAX_AMOUNT = 1000000;

function BuyWater() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { setPurchaseDetails } = usePurchases();
  const [waterPrice, setWaterPrice] = useState({ data: null, loading: true });
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [usageInfo, setUsageInfo] = useState({ data: null, loading: true });
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      amount: 5000,
    },
  });
  const { activeMeterOwnership, meterOwnerships, setActiveMeterOwnership } =
    auth;

  const amount = watch('amount');

  const amountToPurchase = useMemo(
    () => convertAmountToCubicMetres(waterPrice.data, amount),
    [amount, waterPrice?.data],
  );

  const onSubmit = async (data) => {
    const payload = {
      imei: activeMeterOwnership?.meter?.number,
      rechargedValue: amountToPurchase,
    };
    setRechargeLoading(true);
    try {
      const response = await iotAPI.recharge(payload);
      if (response?.status >= 200) {
        setPurchaseDetails({
          amount: data.amount,
        });
        navigate(routes.pay);
      }
    } catch (e) {
      onAPIError(e);
    } finally {
      setRechargeLoading(false);
    }
  };

  const getWaterUsage = async () => {
    try {
      const { data } = await authAPIs.getWaterUsage(activeMeterOwnership.id);
      setUsageInfo({ data: data.data.water_balance });
    } catch (e) {
      setUsageInfo({ loading: false, data: null, error: true });
      onAPIError(e);
    }
  };

  const getWaterPrice = async () => {
    try {
      const { data } = await payments.getWaterPrice(activeMeterOwnership.id);
      setWaterPrice({ data: data.data.price });
    } catch (e) {
      setWaterPrice({ loading: false, data: null, error: true });
      onAPIError(e);
    }
  };

  useEffect(() => {
    if (activeMeterOwnership) {
      getWaterPrice();
      getWaterUsage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMeterOwnership]);

  return (
    <div className="lg:pt-5 flex flex-col space-y-[47px]">
      <div className="hidden lg:block">
        <BuyWaterSteps />
      </div>

      {/* Arrears notification banner */}
      {auth.user?.requires_arrears_payment && (
        <div className="mx-auto max-w-[1004.68px] w-full lg:mr-[50px] px-4 lg:px-0">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 shrink-0">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">
                Outstanding Arrears
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                {auth.user.arrears_reason ||
                  'You have an outstanding balance that requires attention. '}
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                {`Please note that ${auth.user.debt_recovery_percentage || 0}% from your purchases will be used to recover your outstanding balance.`}
              </p>
              <div className="flex flex-wrap gap-4 mt-2">
                <div className="bg-white/70 rounded-lg px-3 py-1.5 border border-red-100">
                  <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold">
                    Balance Due
                  </p>
                  <p className="text-sm font-bold text-red-700">
                    ₦
                    {Number(auth.user.arrears_balance || 0).toLocaleString(
                      undefined,
                      { minimumFractionDigits: 2 },
                    )}
                  </p>
                </div>
                <div className="bg-white/70 rounded-lg px-3 py-1.5 border border-red-100">
                  <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold">
                    Recovery Rate
                  </p>
                  <p className="text-sm font-bold text-red-700">
                    {auth.user.debt_recovery_percentage || '0'}% per purchase
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center">
        <div className="bg-pw-card lg:rounded-[30px] p-4 max-w-[1004.68px] w-full lg:mr-[50px] lg:p-[50px]">
          <div className="pb-[47px] flex items-center justify-between !w-full">
            <BackButton onClick={() => navigate(-1)} />
            <h2 className="font-semibold text-center lg:text-start text-2xl md:text-3xl mx-auto ">
              Buy Water
            </h2>
            {usageInfo ? (
              <div className="min-w-[97.1px]">
                <span className="text-[14px] text-right text-black">
                  Balance: {(usageInfo?.data || 0).toLocaleString()}{' '}
                  {cubicMetres}
                </span>
                {/* <span className="text-[14px] text-right text-black">
                  Balance: {toLitres(usageInfo?.data || 0).toLocaleString()}{" "}
                  litres
                </span> */}
              </div>
            ) : (
              <div className="opacity-0 pointer-events-none">
                <BackButton />
              </div>
            )}
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col lg:flex-row space-y-[18px] lg:space-y-0 lg:space-x-[52.66px] w-full">
              <div className="flex flex-col space-y-1 w-full">
                <div
                  className={getInputClasses(
                    false,
                    'w-full relative flex items-center',
                  )}>
                  <p>{activeMeterOwnership?.meter?.number}</p>
                  {meterOwnerships.length > 1 && (
                    <img
                      src={dropdownIcon}
                      alt="dropdown"
                      className="ml-auto h-4 w-4 flex-shrink-0"></img>
                  )}
                  <select
                    className="absolute top-0 left-0 h-full w-full opacity-0"
                    onChange={(e) => {
                      const value = e.target.value;
                      setActiveMeterOwnership(
                        meterOwnerships.find(
                          (ownership) => ownership.meter.number === value,
                        ),
                      );
                    }}>
                    {meterOwnerships.map((item) => (
                      <option key={item.id}>{item.meter.number}</option>
                    ))}
                  </select>
                </div>
                <span className="text-sm">
                  {activeMeterOwnership?.meter?.tenant?.name}
                </span>
              </div>
              <Controller
                control={control}
                name="amount"
                rules={{ required: true, validate: (v) => v > 0 }}
                render={({ field: { onChange, value } }) => (
                  <NumericFormat
                    value={value}
                    allowNegative={false}
                    onValueChange={(values) =>
                      onChange(Number(values.floatValue))
                    }
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return floatValue < MAX_AMOUNT || !floatValue;
                    }}
                    displayType="input"
                    thousandSeparator
                    decimalScale={2}
                    className={getInputClasses(
                      !!errors.amount,
                      'w-full self-start',
                    )}
                  />
                )}
              />
            </div>
            <div className="mt-[27px] flex flex-col items-center justify-center">
              <div
                className={classNames(
                  'bg-white h-[78px] w-[78px] rounded-full flex items-center justify-center',
                  { '!bg-pw-blue': waterPrice.loading },
                )}>
                {waterPrice.loading ? <Loader /> : <Swap />}
              </div>
              <div className="pt-[14px] px-3 mt-6 max-w-[80vw] lg:min-w-[523px] border-[2px] border-[#C9C9C9] rounded-[20px] flex flex-col space-y-[17px]">
                <p className="textpwpw-grey2 text-sm font-medium text-center mb-8">
                  The equivalent Amount Of water you will get in cubic metres
                </p>
                <NumericFormat
                  displayType="text"
                  thousandSeparator
                  decimalScale={2}
                  allowNegative={false}
                  value={amountToPurchase}
                  decimalSeparator="."
                  fixedDecimalScale
                  className="textpw-dark text-xl lg:text-[96px] lg:pb-[80px] pb-[30px] font-semibold text-center"
                />
              </div>
              <p className="textpwpw-grey2  mt-[19px] text-center font-medium text-[17px]">
                <NumericFormat
                  value={waterPrice?.data}
                  fixedDecimalScale
                  thousandSeparator
                  displayType="text"
                  decimalScale={2}
                />{' '}
                Naira = 1 {cubicMetres}
              </p>
              <div className="mt-[47px] flex items-center justif-center">
                <Button
                  text={'Buy water credit'}
                  type="submit"
                  loading={waterPrice.loading || rechargeLoading}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BuyWater;
